import { action, computed, makeObservable } from 'src_common/common/mobx-wrapper';
import { MobxValue } from 'src_common/common/mobx-utils/MobxValue';
import {
    openapiProxySessionCustomerLoginRequest,
    OpenapiProxySessionCustomerLoginResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_session_customer_login';
import { openapiProxySessionPingRequest } from 'src/api_openapi/generated/openapi_proxy_session_ping';
import {
    openapiProxySessionStaffLoginRequest,
    OpenapiProxySessionStaffLoginResponse200Type,
} from 'src/api_openapi/generated/openapi_proxy_session_staff_login';
import * as t from 'io-ts';
import { createGuard } from 'src_common/common/createGuard';
import { createAnonymousSession } from 'src_common/server/api/createAnonymousSession';
import { UniverseType } from 'src_common/common/universe';
import { createProgramSession } from 'src_common/server/createProgramSession';
import { OpenapiProxySessionProgramLoginResponse200Type } from 'src_common/server/openapi_proxy_session_program_login';
import { Value } from 'src_common/common/mobx-utils/Value';
import { DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS, DEFAULT_ACTIVITY_PING_INTERVAL_MS, startActivityBasedPing } from './sessionActivityPing';
import { fetchSessionActivityInactivityThresholdMs, SessionActivityPingAccountType } from './fetchSessionActivityPingConfig';
import { SessionPingDeviceInfoType } from './sessionPingDeviceInfo';

const JwtDataIO = t.interface({
    sub: t.string,
    nbf: t.number,
    exp: t.number,
    st: t.union([t.string, t.undefined, t.null]),
    sc: t.string,
    sn: t.string,
    ip: t.union([t.string, t.undefined, t.null]),
});

export type JwtDataType = t.TypeOf<typeof JwtDataIO>;

const isJwtData = createGuard(JwtDataIO);

export type LoginResponseType = OpenapiProxySessionCustomerLoginResponse200Type;

const decodedJwtData = (jwt: string, mode: 'browser' | 'server' | 'native'): JwtDataType | null => {
    if (jwt !== '') {
        const chunks = jwt.split('.');

        if (chunks.length !== 3) {
            console.error('decodedJwtData1: three parts were expected');
            return null;
        }

        const jwtData = chunks[1];

        if (jwtData === undefined) {
            console.error('decodedJwtData2: second part missing');
            return null;
        }

        try {
            const data = JSON.parse(atob(jwtData));

            if (isJwtData(data)) {
                return data;
            }

            if (mode === 'browser') {
                console.error(data);
            }
        } catch (error) {
            console.error('decodedJwtData3: json expected', { error, jwtData });
            return null;
        }

        return null;
    }

    return null;
};

interface ProgramSessionPingOptions {
    maintainSessionType?: 'program' | 'customer';
    sessionRecoverCallback: () => Promise<null | OpenapiProxySessionProgramLoginResponse200Type>;
    shouldRefreshSession?: boolean;
}

interface SessionPingCallOptions {
    deviceInfo?: SessionPingDeviceInfoType;
    programOptions?: ProgramSessionPingOptions;
}

interface ParamsType {
    readonly websocket_api_host: string; // TODO remove and change to envVariabled
    readonly websocketUrl: string; // TODO remove and change to envVariabled
    readonly apiTimeout: number;
    readonly getInitJwt: () => string | null;
    readonly setJwt: (jwt: string) => void; //side effect
    readonly startPing: boolean;
    readonly activityBasedPingEnabled?: boolean;
    readonly cacheApiCall: Record<string, string>; // TODO remove and change to envVariabled
    readonly refreshingJwtFromCookie?: boolean; //TODO - to be removed in the near future
    readonly mode?: 'browser' | 'server';
    readonly pingOptions?: ProgramSessionPingOptions;
    readonly activityPingIntervalMs?: number;
    readonly activityInactivityThresholdMs?: number;
    readonly getPingDeviceInfo?: () => SessionPingDeviceInfoType;
    readonly shouldKeepPinging?: () => boolean;
}

export class JwtValue {
    private readonly setJwt: (jwt: string) => void;
    private readonly current: MobxValue<string>;

    public constructor(initValue: string, setJwt: (jwt: string) => void) {
        this.setJwt = setJwt;
        this.current = MobxValue.create({
            initValue,
        });

        setJwt(initValue);
    }

    public setValue(newJwt: string): void {
        this.current.setValue(newJwt);
        this.setJwt(newJwt);
    }

    public get(): string {
        return this.current.getValue();
    }
}

type ApiResponseType<R> =
    | {
          status: 200;
          body: R;
      }
    | {
          status: 400;
          body: string;
      }
    | {
          status: 401;
          body: string;
      }
    | {
          status: 500;
          body: unknown;
      };

interface SessionApiType {
    call<P, R>(
        method: (
            api_url: string,
            api_timeout: number,
            backendToken: string,
            params: P,
            extraHeaders: Record<string, string>
        ) => Promise<ApiResponseType<R>>,
        params: P,
        extraHeaders?: Record<string, string>
    ): Promise<R>;

    ping(options?: SessionPingCallOptions): Promise<void>;
}

class SessionApiFake {
    public async call<P, R>(
        _method: (
            api_url: string,
            api_timeout: number,
            backendToken: string,
            params: P,
            extraHeaders: Record<string, string>
        ) => Promise<ApiResponseType<R>>,
        _params: P
    ): Promise<R> {
        return new Promise(() => {});
    }

    public async ping(): Promise<void> {}
}

class SessionApiRest {
    private readonly apiUrl: string;
    private readonly apiTimeout: number;
    private readonly jwtValue: JwtValue;

    public constructor(apiUrl: string, apiTimeout: number, jwtValue: JwtValue) {
        this.apiUrl = apiUrl;
        this.apiTimeout = apiTimeout;
        this.jwtValue = jwtValue;
    }

    public async call<P, R>(
        method: (
            api_url: string,
            api_timeout: number,
            backendToken: string,
            params: P,
            extraHeaders: Record<string, string>
        ) => Promise<ApiResponseType<R>>,
        params: P,
        extraHeaders: Record<string, string>
    ): Promise<R> {
        const result = await method(this.apiUrl, this.apiTimeout, this.jwtValue.get(), params, extraHeaders);
        const status = result.status;

        if (result.status === 200) {
            return result.body;
        }

        if (result.status === 400) {
            console.error('SessionApi: User error', result.body);
            throw Error('SessionApi: User error');
        }

        if (result.status === 401) {
            console.error('SessionApi: 401', result.body);
            await this.ping();
        }

        if (result.status === 500) {
            console.error('SessionApi: Internal server error', result.body);
            throw Error('SessionApi: Internal server error');
        }

        throw new Error(`Unhandled response ${status}`);
    }

    public async ping(options?: SessionPingCallOptions): Promise<void> {
        const deviceInfo = options?.deviceInfo;
        const programOptions = options?.programOptions;

        const response = await openapiProxySessionPingRequest(this.apiUrl, this.apiTimeout, this.jwtValue.get(), {
            requestBody:
                deviceInfo === undefined
                    ? {}
                    : {
                          mobileApp: deviceInfo.mobile_app,
                          deviceInformation: deviceInfo.device_information ?? undefined,
                      },
        });
        if (response.status === 200) {
            const new_jwt = response.body.new_jwt ?? null;

            if (new_jwt !== null) {
                const decodedNewJwt = decodedJwtData(new_jwt, 'server');

                if (Session.blockLogout && (decodedNewJwt?.st === 'anonymous' || decodedNewJwt?.sub === '')) {
                    return;
                }

                if (programOptions === undefined) {
                    this.jwtValue.setValue(new_jwt);
                    return;
                }

                const userRole = decodedNewJwt?.st ?? '';
                if (programOptions.shouldRefreshSession !== true && programOptions.maintainSessionType === userRole) {
                    this.jwtValue.setValue(new_jwt);
                } else {
                    console.warn('SessionApi: Session type mismatch. Retry login as program');
                    const response = (await programOptions.sessionRecoverCallback()) ?? null;
                    if (response === null || response.type !== 'CreateSessionResponseOk') {
                        console.error('SessionApi Ping: Failed to create program session');
                    } else {
                        console.info(
                            'SessionApi Ping: Session was regenerated from  maintainSessionType: ',
                            programOptions.maintainSessionType
                        );
                        this.jwtValue.setValue(response.access_token);
                    }
                }
            }
        } else {
            throw Error('Response code 200 expected');
        }
    }
}

type EphemeralAccessType = {
    ephemeralToken: JwtValue;
    accountId: string;
} | null;

export class Session {
    /**
     * @description When true, prevents ping() from replacing user JWT with anonymous JWT.
     */
    public static blockLogout: boolean = false;

    public readonly websocketUrl: string;
    private readonly mode: 'browser' | 'server' | 'native';
    private readonly jwtValue: JwtValue;
    private readonly api: SessionApiType;
    public cacheApiCall: Record<string, string>;

    /**
     * @description Ephemeral token is generated in the gpp-accounts-service to perform pre-login operations after account creation.
     * Its lifetime and usage are limited.
     */
    private readonly ephemeralAccess: Value<EphemeralAccessType | null>;
    private readonly getPingDeviceInfoCallback: (() => SessionPingDeviceInfoType) | undefined;
    private stopActivityBasedPing: (() => void) | null = null;
    private timerParams: ParamsType | null = null;

    private constructor(
        websocketUrl: string,
        mode: 'browser' | 'server' | 'native',
        jwtValue: JwtValue,
        api: SessionApiType,
        cacheApiCall: Record<string, string>,
        getPingDeviceInfo?: () => SessionPingDeviceInfoType
    ) {
        this.websocketUrl = websocketUrl;
        this.mode = mode;
        this.jwtValue = jwtValue;
        this.api = api;
        this.cacheApiCall = cacheApiCall;
        this.ephemeralAccess = new Value(null);
        this.getPingDeviceInfoCallback = getPingDeviceInfo;

        makeObservable(this);
    }

    public static startServerAnonymousSession = async (
        apiUrl: string,
        apiTimeout: number,
        themeUniverse: UniverseType
    ): Promise<Session | null> => {
        console.info('Starting anonymous session');
        const response = await createAnonymousSession(apiUrl, apiTimeout, themeUniverse);

        if (response === null) {
            console.error('Session: Failed to create anonymous session');
            return null;
        }
        const jwtValue = new JwtValue(response, () => {});
        const api = new SessionApiFake();
        return new Session('', 'server', jwtValue, api, {}, undefined);
    };

    public static startServerProgramSession = async (
        websocket_api_host: string,
        websocketUrl: string,
        apiTimeout: number,
        apiUser: string,
        apiPassword: string
    ): Promise<Session | null> => {
        console.info('Starting program session');
        const apiUrl = websocket_api_host;

        const response = await createProgramSession(apiUrl, apiTimeout, apiUser, apiPassword);

        if (response === null || response.type !== 'CreateSessionResponseOk') {
            console.error('Session: Failed to create program session');
            return null;
        }

        const jwtValue = new JwtValue(response.access_token, () => {});
        const api = new SessionApiRest(apiUrl, apiTimeout, jwtValue);
        const session = new Session(websocketUrl, 'server', jwtValue, api, {}, undefined);

        session.startTimers({
            websocket_api_host,
            websocketUrl,
            apiTimeout,
            getInitJwt: () => null,
            setJwt: () => {},
            cacheApiCall: {},
            startPing: true,
            mode: 'server',
            pingOptions: {
                sessionRecoverCallback: () => createProgramSession(apiUrl, apiTimeout, apiUser, apiPassword),
                maintainSessionType: 'program',
            },
        });

        return session;
    };

    public static startFakeSession(): Session {
        const jwtValue = new JwtValue('', () => {});
        const api = new SessionApiFake();
        return new Session('', 'server', jwtValue, api, {}, undefined);
    }
    private getActivityPingAccountType(): SessionActivityPingAccountType {
        return this.decodedJwtData?.st === 'customer' ? 'customer' : 'anonymous';
    }

    private stopActivityBasedPingTimers(): void {
        if (this.stopActivityBasedPing !== null) {
            this.stopActivityBasedPing();
            this.stopActivityBasedPing = null;
        }
    }

    private startActivityBasedPingTimers(params: ParamsType): void {
        const pingIntervalMs = params.activityPingIntervalMs ?? DEFAULT_ACTIVITY_PING_INTERVAL_MS;
        const inactivityThresholdMs =
            params.activityInactivityThresholdMs ?? DEFAULT_ACTIVITY_INACTIVITY_THRESHOLD_MS;

        this.stopActivityBasedPing = startActivityBasedPing(
            pingIntervalMs,
            inactivityThresholdMs,
            () => this.performPing(params),
            params.shouldKeepPinging
        );
    }

    private async refreshActivityPingInactivityThreshold(accountType: SessionActivityPingAccountType): Promise<void> {
        if (this.timerParams === null || this.timerParams.activityBasedPingEnabled !== true || !this.isBrowser()) {
            return;
        }

        const inactivityThresholdMs = await fetchSessionActivityInactivityThresholdMs({
            jwt: this.jwtValue.get(),
            accountType,
        });

        this.timerParams = {
            ...this.timerParams,
            activityInactivityThresholdMs: inactivityThresholdMs,
        };

        this.stopActivityBasedPingTimers();
        this.startActivityBasedPingTimers(this.timerParams);
    }

    private startTimers(params: ParamsType): void {
        if (params.refreshingJwtFromCookie === true) {
            setInterval(async () => {
                const jwt = params.getInitJwt();
                const prevJwt = this.jwtValue.get();

                if (jwt !== null && jwt !== prevJwt) {
                    console.info('Init JWT from cookie');
                    this.jwtValue.setValue(jwt);
                }
            }, 1000);
        }

        if (params.startPing === false) {
            return;
        }

        if (params.activityBasedPingEnabled === true && this.isBrowser()) {
            this.timerParams = params;
            this.startActivityBasedPingTimers(params);
            return;
        }

        setInterval(async () => {
            await this.performPing(params);
        }, 60 * 1000);
    }

    private getPingDeviceInfo(): SessionPingDeviceInfoType | undefined {
        return this.getPingDeviceInfoCallback?.();
    }

    private buildPingCallOptions(programOptions?: ProgramSessionPingOptions): SessionPingCallOptions | undefined {
        const deviceInfo = this.getPingDeviceInfo();

        
        if (programOptions === undefined && deviceInfo === undefined) {
            return undefined;
        }

        return {
            deviceInfo,
            programOptions,
        };
    }
    private async performPing(params: ParamsType): Promise<void> {
        const decodedData = this.decodedJwtData;

        if (decodedData === null) {
            console.warn('No decoded JWT data found. Pinging to recover session.');
            await this.api.ping(this.buildPingCallOptions(params.pingOptions));
            return;
        }

        if (params.pingOptions === undefined) {
            await this.api.ping(this.buildPingCallOptions());
            return;
        }

        const currentTimeInSeconds = Math.floor(Date.now() / 1000);
        const expirationTimeInSeconds = decodedData.exp;
        const timeLeftInSeconds = expirationTimeInSeconds - currentTimeInSeconds;
        const shouldRefreshSession = timeLeftInSeconds <= 60;

        if (shouldRefreshSession) {
            console.warn('Token is about to expire. Refreshing session via ping.');
        }

        await this.api.ping(
            this.buildPingCallOptions({
                shouldRefreshSession: shouldRefreshSession,
                sessionRecoverCallback: params.pingOptions.sessionRecoverCallback,
                maintainSessionType: params.pingOptions.maintainSessionType,
            })
        );
    }

    public static async start(params: ParamsType): Promise<Session> {
        const apiUrl = params.websocket_api_host;
        const apiTimeout = params.apiTimeout;
        const initJwt = params.getInitJwt();
        const setJwt = params.setJwt;

        const jwtValue = new JwtValue(initJwt ?? '', setJwt);
        const api = new SessionApiRest(apiUrl, apiTimeout, jwtValue);
        const mode = params.mode ?? 'browser';
        const session = new Session(params.websocketUrl, mode, jwtValue, api, params.cacheApiCall, params.getPingDeviceInfo);

        await api.ping(session.buildPingCallOptions());

        let timerParams = params;

        if (params.activityBasedPingEnabled === true && mode === 'browser') {
            const accountType = session.getActivityPingAccountType();
            const inactivityThresholdMs = await fetchSessionActivityInactivityThresholdMs({
                jwt: jwtValue.get(),
                accountType,
            });

            timerParams = {
                ...params,
                activityInactivityThresholdMs: inactivityThresholdMs,
            };
        }

        session.startTimers(timerParams);
        return session;
    }

    public isBrowser(): boolean {
        return this.mode === 'browser';
    }

    public async loginCustomer(
        username: string,
        password: string,
        disableGeo: boolean
    ): Promise<OpenapiProxySessionCustomerLoginResponse200Type> {
        const response = await this.api.call(openapiProxySessionCustomerLoginRequest, {
            requestBody: {
                username,
                password,
                disable_geo: disableGeo === false ? undefined : true,
            },
        });

        if (response.type === 'CreateSessionResponseOk') {
            this.jwtValue.setValue(response.access_token);
            await this.refreshActivityPingInactivityThreshold('customer');
        }

        return response;
    }

    public async loginStaff(username: string, password: string): Promise<OpenapiProxySessionStaffLoginResponse200Type> {
        const response = await this.api.call(openapiProxySessionStaffLoginRequest, {
            requestBody: {
                username,
                password,
            },
        });

        if (response.type === 'CreateSessionResponseOk') {
            this.jwtValue.setValue(response.access_token);
        }

        return response;
    }

    public async logout(): Promise<void> {
        throw Error('TODO');
    }

    private isServer = (): boolean => typeof window === 'undefined';

    public async call<P, R>(
        method: (
            api_url: string,
            api_timeout: number,
            backendToken: string,
            params: P,
            extraHeaders: Record<string, string>
        ) => Promise<ApiResponseType<R>>,
        params: P,
        extraHeaders?: Record<string, string>
    ): Promise<R> {
        const key = `${method.name}-${JSON.stringify(params)}`;
        const isServer = this.isServer();

        if (isServer === false) {
            const cacheValue = this.cacheApiCall[key];

            if (cacheValue !== undefined) {
                try {
                    const response = JSON.parse(cacheValue);
                    return response;
                } catch (error) {
                    console.error('client parse error', error);
                }
            }
        }

        const result = await this.api.call(method, params, extraHeaders);

        if (isServer) {
            this.cacheApiCall[key] = JSON.stringify(result);
        }

        return result;
    }

    public get currentJwt(): string {
        return this.jwtValue.get();
    }

    @computed.struct public get ephemeralAccessValue(): EphemeralAccessType | null {
        return this.ephemeralAccess.getValue();
    }

    @action public setEphemeralAccess = (token: string | null): void => {
        if (token === null) {
            this.ephemeralAccess.setValue(null);
            return;
        }

        const decodedJwt = decodedJwtData(token, 'browser');

        if (decodedJwt === null) {
            this.ephemeralAccess.setValue(null);
            return;
        }

        this.ephemeralAccess.setValue({
            ephemeralToken: new JwtValue(token, () => {}),
            accountId: decodedJwt.sub,
        });
    };

    @computed public get isEphemeralActive(): boolean {
        return this.ephemeralAccessValue !== null;
    }

    @computed.struct public get decodedJwtData(): JwtDataType | null {
        return decodedJwtData(this.currentJwt, this.mode);
    }

    @computed.struct public get userId(): number | null {
        if (this.decodedJwtData === null) {
            return null;
        }

        if (this.decodedJwtData.sub === '') {
            return null;
        }

        if (this.decodedJwtData.st === 'program') {
            return null;
        }

        const subNum = parseInt(this.decodedJwtData.sub, 10);

        if (isNaN(subNum)) {
            console.error('userId NaN');
            return null;
        }
        return subNum;
    }

    @computed.struct public get ipUser(): string | null {
        if (this.decodedJwtData === null) {
            return null;
        }

        return this.decodedJwtData.ip ?? null;
    }

    @computed.struct public get isAuthorized(): boolean {
        return this.userId !== null;
    }

    @computed.struct public get userIpCountry(): string | null {
        const decodedData = this.decodedJwtData;
        if (decodedData instanceof Error) {
            console.error(decodedData);
            return null;
        }

        if (decodedData === null || decodedData.sc === '') {
            return null;
        }

        return decodedData.sc;
    }

    @computed.struct public get loggedTime(): number | null {
        if (this.decodedJwtData !== null) {
            return this.decodedJwtData.nbf * 1000;
        }
        return null;
    }

    @computed.struct public get loggedUserName(): string | null {
        if (this.decodedJwtData !== null) {
            return this.decodedJwtData.sn;
        }

        return null;
    }

    public clearCache = (): void => {
        this.cacheApiCall = {};
    };
}
